import React from 'react'
import { groupBy, camelCase, flatten } from 'lodash'
import { standardColumns, codingAnomalies, byAccountAnomalies } from '../constants'
import { DataSection, Anomaly, Column } from '../types'
import { ConfigSection } from '../../../shared/types'
import { TableConnected, Table } from 'elements'
import ReportTable from '../view/common/ReportTable'
import { applyConfigDataSections } from './templates'

const { Row, Cell } = Table.Data

type GetDataSectionsProps = {
  anomaly: Anomaly
  context: 'table' | 'export'
  configSections?: ConfigSection[]
  reportEndDate: string
}

type AnomalyTableOptionsProps = { anomaly: Anomaly; columns?: Column[] }

function getAnomalyTableOptions(props: AnomalyTableOptionsProps) {
  const { anomaly, columns } = props

  const renderRowSubComponent = (row: any) => {
    if (row.original.data) {
      const dataSection = {
        data: row.original.data,
        columns: columns || standardColumns,
        tableOptions: { highlightedColumn: 'Name' },
      }

      return (
        <Row bg="background.1">
          <Cell colSpan={row.cells.length}>
            <ReportTable dataSection={dataSection} />
          </Cell>
        </Row>
      )
    }

    return null
  }

  if (codingAnomalies.includes(camelCase(anomaly.anomalyName))) {
    return { kind: Table.Card, noHeader: true, renderRowSubComponent }
  }

  return { renderRowSubComponent, highlightedColumn: 'Name' }
}

export function getAnomalyDataSections(props: GetDataSectionsProps): DataSection[] {
  const { anomaly, context, configSections, reportEndDate } = props
  const key = camelCase(anomaly.anomalyName)

  let dataSections: DataSection[] = []

  //for these anomalies, we want the data sections grouped by account
  if (byAccountAnomalies.includes(anomaly.anomalyName)) {
    const transactionsByAccount = groupBy(anomaly.transactions, 'transactionAccount')

    dataSections = Object.keys(transactionsByAccount).map((account) => ({
      data: transactionsByAccount[account],
      title: account,
      key,
      columns: standardColumns,
    }))
  } else if (codingAnomalies.includes(camelCase(anomaly.anomalyName))) {
    dataSections = anomaly.data!.map((entity) => {
      const columns = [
        {
          Header: 'Account',
          accessor: 'account',
          //@ts-ignore
          Cell: TableConnected.ExpandSubComponentCell,
          style: { text: { fontWeight: 'bold' } },
        },
        {
          Header: 'Count',
          accessor: 'count',
          //@ts-ignore
          Cell: TableConnected.StandardCell,
        },
      ]

      const section = {
        key,
        title: entity.entityName,
        data: entity.accountTransactions.map((account: any) => ({
          account: account.accountName,
          count: account.transactions.length,
          data: account.transactions,
        })),
        columns,
        onRowClick: (row: any) => row.toggleRowExpanded(),
      }

      if (context === 'table') {
        return section
      }

      //for export, return flat list of lines instead of grouped by account
      return { ...section, data: flatten(section.data.map((datum: any) => datum.data)) }
    })
  } else {
    dataSections = [
      {
        data: anomaly.transactions!,
        key,
        columns: standardColumns,
      },
    ]
  }

  const configuredSections = applyConfigDataSections({
    configSections,
    dataSections,
    reportEndDate,
  })

  return configuredSections.map((section) => ({
    ...section,
    table: true,
    tableOptions: getAnomalyTableOptions({ anomaly }),
  }))
}
